import { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileUp,
  GripVertical,
  Info,
  Link2,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMappableProfileFields } from '@/hooks/use-mappable-profile-fields';
import type { PreboardingFormField } from '@hygge/shared';

interface FieldRowProps {
  field: PreboardingFormField;
  onEdit: () => void;
  onDelete: () => void;
}

export function FieldRow({ field, onEdit, onDelete }: FieldRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const { data: catalog } = useMappableProfileFields();

  const mappingLabel = useMemo(() => {
    if (field.kind !== 'MAPPED') return null;
    const entry = (catalog ?? []).find((f) => f.key === field.mappingKey);
    if (!entry) return field.mappingKey ?? 'Unmapped';
    return entry.breadcrumb.length ? `${entry.label} · ${entry.breadcrumb.join(' → ')}` : entry.label;
  }, [catalog, field]);

  const isMapped = field.kind === 'MAPPED';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-start gap-2 rounded-md border border-border/60 bg-background px-2.5 py-2 hover:border-border"
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-40 group-hover:opacity-100 cursor-grab active:cursor-grabbing touch-none transition-opacity"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <Badge variant={isMapped ? 'info' : 'secondary'} className="gap-1 px-1.5 py-0 text-[10px]">
            {isMapped ? <Link2 className="h-2.5 w-2.5" /> : <FileUp className="h-2.5 w-2.5" />}
            {isMapped ? 'Field' : 'Document'}
          </Badge>
          <span className="truncate text-sm font-medium text-foreground">{field.label}</span>
          {field.required && (
            <span className="text-xs font-semibold text-destructive" aria-label="Required">
              *
            </span>
          )}
          {field.helperText && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-muted-foreground/70 hover:text-foreground">
                    <Info className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{field.helperText}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <span className="truncate text-[11px] text-muted-foreground">
          {isMapped ? `Maps to ${mappingLabel}` : `Uploads to “${field.targetFolder}”`}
        </span>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Field actions"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Edit field
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Delete field
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
